import { Component } from '@angular/core'
import { Store } from '@ngrx/store'
import { MarkerDimension } from '../../../state/case/case.state'
import { plotDataActions } from '../../../state/plot-data/plot-data.actions'
import { PlotAxis } from '../../../state/plot-data/plot-data.state'
import {
    selectMarkerX,
    selectMarkerY,
    selectMarkerColor,
} from '../../../state/plot-data/plot-data.selectors'
import {
    selectHasMarkers,
    selectCaseMarkers,
    selectCaseSegObjectPropertyMap,
} from '../../../state/case/case.selectors'
import { DownloadService } from '../../../shared/services/download.service'
import { HttpErrorResponse } from '@angular/common/http'
import { ToasterService } from '../../../shared/services/toaster.service'
import { BehaviorSubject, map, take } from 'rxjs'
import {
    selectSavedFreeSelectionNamesBySegSource,
    selectSavedHierarchicalSelectionNodeItems,
    selectSavedUnsupervisedSelectionNodeItems,
    selectSelectionState,
    selectDisplayedSelectionsOnScatterPlot,
} from '../../../state/selection/selection.selectors'
import { selectionActions } from 'src/app/state/selection/selection.actions'
import { SelectionBase, SelectionType } from 'src/app/state/selection/selection.state'

interface TreeNode {
    id: string
    show: boolean
    selected: boolean
    nodes: TreeNode[]
    isTopLevel?: boolean
}

@Component({
    selector: 'app-plot-data-selection',
    templateUrl: './plot-data-selection.component.html',
    styleUrls: ['./plot-data-selection.component.scss'],
})
export class PlotDataSelectionComponent {
    readonly markerXAxis$ = this.store.select(selectMarkerX)
    readonly markerYAxis$ = this.store.select(selectMarkerY)
    readonly markerColorAxis$ = this.store.select(selectMarkerColor)
    markerColorAxis: string = ''
    readonly availableMarkers$ = this.store.select(selectCaseMarkers)
    readonly availableColorMarkers$ = this.store
        .select(selectCaseMarkers)
        .pipe(map(markers => (markers ? [{ name: 'None', id: null }, ...markers] : [])))
    readonly hasMarkers$ = this.store.select(selectHasMarkers)
    readonly caseSegObjectPropertyMap$ = this.store.select(selectCaseSegObjectPropertyMap)
    readonly selectionState$ = this.store.select(selectSelectionState)
    private selectionNames$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([])
    readonly savedFreeSelectionNames$ = this.store.select(selectSavedFreeSelectionNamesBySegSource)
    readonly savedHierarchicalSelectionNodeItems$ = this.store.select(
        selectSavedHierarchicalSelectionNodeItems
    )
    readonly savedUnsupervisedSelectionNodeItems$ = this.store.select(
        selectSavedUnsupervisedSelectionNodeItems
    )
    readonly displayedSelections$ = this.store.select(selectDisplayedSelectionsOnScatterPlot)

    private selectionType: SelectionType = SelectionType.Free

    selectedBoxes: string[] = []
    selectionNodes: TreeNode[] = [
        {
            id: 'Second Tree Root',
            show: false,
            selected: false,
            isTopLevel: true,
            nodes: [{ id: 'selections', show: false, selected: false, nodes: [] }],
        },
    ]

    constructor(
        private readonly store: Store,
        private readonly toaster: ToasterService,
        private readonly downloader: DownloadService
    ) {
        this.selectionState$.pipe().subscribe(selectionState => {
            if (this.selectionType !== selectionState.type) {
                this.selectionType = selectionState.type
                this.selectedBoxes = []
            }

            if (selectionState.type === SelectionType.Free) {
                this.savedFreeSelectionNames$.pipe(take(1)).subscribe(names => {
                    this.selectionNames$.next(names)
                })
            } else if (selectionState.type === SelectionType.Hierarchical) {
                this.savedHierarchicalSelectionNodeItems$.pipe(take(1)).subscribe(items => {
                    this.selectionNames$.next(items.map(item => item.name!))
                })
            } else {
                this.savedHierarchicalSelectionNodeItems$.pipe(take(1)).subscribe(items => {
                    this.selectionNames$.next(
                        items
                            .map(item => item.name!)
                            .filter(name =>
                                name.includes(
                                    (selectionState.resolution / 10).toFixed(1).toString()
                                )
                            )
                    )
                })
            }

            this.selectionNames$.subscribe(names => {
                const selections = names.map(name => ({
                    id: name,
                    show: false,
                    selected: [...selectionState.displayedOnScatterPlot]!.find(
                        (s: SelectionBase) => s.name === name
                    )
                        ? true
                        : false,
                    nodes: [],
                }))
                this.selectionNodes = [
                    {
                        id: 'Displayed selections',
                        show: selections.length > 5 ? false : true,
                        selected: false,
                        nodes: selections,
                        isTopLevel: true,
                    },
                ]
            })
        })

        this.displayedSelections$.pipe().subscribe(currentSelections => {
            this.selectedBoxes = currentSelections.map(s => s.name!)
        })
    }

    setDataXAxis(marker: MarkerDimension): void {
        if (!marker) return
        this.store.dispatch(
            plotDataActions.loadDataForAxis({
                marker,
                axis: PlotAxis.X,
            })
        )
    }

    setDataYAxis(marker: MarkerDimension): void {
        if (!marker) return
        this.store.dispatch(
            plotDataActions.loadDataForAxis({
                marker,
                axis: PlotAxis.Y,
            })
        )
    }

    setDataColorAxis(marker: MarkerDimension): void {
        this.markerColorAxis = marker?.name === 'None' ? '' : marker?.name
        this.store.dispatch(
            plotDataActions.loadDataForAxis({
                marker: marker?.name === 'None' ? null : marker,
                axis: PlotAxis.COLOR,
            })
        )
        if (this.markerColorAxis) {
            const tree = this.selectionNodes[0]
            this.selectionNodes = [{ ...tree, show: false }]
        } else {
            this.store.dispatch(
                selectionActions.setVisibleSelectionsOnScatterPlot({
                    selectionNames: this.selectedBoxes,
                })
            )
        }
    }

    toggleSelection(name: string): void {
        if (this.selectedBoxes.includes(name)) {
            this.selectedBoxes = this.selectedBoxes.filter(box => box !== name)
        } else {
            this.selectedBoxes = [...this.selectedBoxes, name]
        }

        this.store.dispatch(
            selectionActions.setVisibleSelectionsOnScatterPlot({
                selectionNames: this.selectedBoxes,
            })
        )
    }

    selectedBox(name: string): boolean {
        return this.selectedBoxes.includes(name)
    }

    selectSelection(node: TreeNode) {
        node.selected = !node.selected
        if (node.nodes.length > 0) {
            this.updateChildNodes(node.nodes, node.selected)
        }

        if (this.selectedBoxes.includes(node.id)) {
            this.selectedBoxes = this.selectedBoxes.filter(box => box !== node.id)
        } else {
            this.selectedBoxes = [...this.selectedBoxes, node.id]
        }
        this.store.dispatch(
            selectionActions.setVisibleSelectionsOnScatterPlot({
                selectionNames: this.selectedBoxes,
            })
        )
    }

    toggleNode(node: TreeNode) {
        node.show = !node.show
    }

    selectNode(node: TreeNode) {
        node.selected = !node.selected
        if (node.nodes.length > 0) {
            this.updateChildNodes(node.nodes, node.selected)
        }
    }

    private updateChildNodes(nodes: TreeNode[], selected: boolean) {
        nodes.forEach(node => {
            node.selected = selected
            if (node.nodes.length > 0) {
                this.updateChildNodes(node.nodes, selected)
            }
        })
    }

    downloadMarkers(): void {
        this.caseSegObjectPropertyMap$.pipe(take(1)).subscribe(mapName => {
            this.downloader.downloadMarkers(mapName!.name).subscribe({
                error: (err: HttpErrorResponse) => {
                    this.toaster.httpError(err, 'Failed to download markers.')
                },
            })
        })
    }

    hasChildren(node: TreeNode): boolean {
        return node.nodes && node.nodes.length > 0
    }
}
